import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useDataContext } from 'context/DataStoreContext';
import { useApi } from 'model/Api';
import { User } from 'model/User';
import { Ledger } from 'model/Ledger';
import { Contractor } from 'model/Contractor';
import { generalStyle } from 'helper/Style';
import { convertCosts, convertDate, convertUserName } from 'helper/Converter';
import Layout from 'component/part/Layout';
import BackLink from 'component/part/BackLink';
import H2 from 'component/part/H2';
import List, { ListIndex } from 'component/part/List';

type ListLedger = Ledger & {
  leaderName: string;
  safetyOfficerName: string;
  primeContractorName: string;
  totalCosts: ReturnType<typeof convertCosts>,
  grossProfit: number;
};

const ledgerConditions = { with: ['cost'] };

const ledgerIndexes: ListIndex[] = [
  { label: '現場名', key: 'name' },
  { label: '発注元', key: 'orderingParty' },
  { label: '略称', key: 'shortName' },
  { label: '上長', key: 'safetyOfficerId' },
  { label: '現場代理人', key: 'leaderId' },
  { label: '弊社次数', key: 'order' },
  { label: '元請', key: 'primeContractorId' },
  { label: '請負協力業者' },
  { label: '工期', key: 'endDate' },
  { label: '請負金額', key: 'price', align: 'right' },
  { label: '人工', align: 'right' },
  { label: '機械', align: 'right' },
  { label: '産廃', align: 'right' },
  { label: '経費', align: 'right' },
  { label: '費用小計', align: 'right' },
  { label: '粗利', align: 'right' },
];

const style = {
  table: {
    whiteSpace: 'nowrap' as const,
    fontSize: '1.4rem',
  },
};

const ScheduleList: React.FC = () => {
  const { state } = useDataContext();
  const { list = [] } = state?.ledger || {};
  const api = useApi();
  const [users, setUsers] = useState<User[]>([]);
  const [ledgers, setLedgers] = useState<ListLedger[]>([]);
  const [contractors, setContractors] = useState<Contractor[]>([]);

  useEffect(() => {
    (async () => {
      const [usersResponse, contractorsResponse] = await Promise.all([
        api.user.getsByOwn(),
        api.contractor.getsByOwn(),
      ]);
      setUsers(usersResponse);
      setContractors(contractorsResponse);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const listLedgers: ListLedger[] = list.map((ledger) => {
        const leader = users.find((user) => user.id === ledger.leaderId);
        const safetyOfficer = users.find((user) => user.id === ledger.safetyOfficerId);
        const contractor = contractors.find((contractor) => contractor.id === ledger.primeContractorId);
        const totalCosts = convertCosts(ledger.costs);

        return {
          ...ledger,
          leaderName: leader ? convertUserName(leader) : '',
          safetyOfficerName: safetyOfficer ? convertUserName(safetyOfficer) : '',
          primeContractorName: contractor?.name || '',
          totalCosts,
          grossProfit: ledger.price - totalCosts.total,
        };
      });
      setLedgers(listLedgers);
    })();
  }, [list]);

  const rows = ledgers.map((ledger) => [
    <NavLink to={`/ledger/${ledger.id}`} css={generalStyle.link}>{ledger.name}</NavLink>,
    ledger.orderingParty,
    ledger.shortName,
    ledger.safetyOfficerName,
    ledger.leaderName,
    ledger.order ? `${ledger.order}次` : '元請',
    ledger.primeContractorName,
    (ledger.members || []).map((partner, i) => <div key={i}>{partner.name || ''}</div>),
    `${convertDate(ledger.startDate, 'list')}〜${convertDate(ledger.endDate, 'list')}`,
    `¥${ledger.price.toLocaleString()}`,
    `¥${ledger.totalCosts.personal.toLocaleString()}`,
    `¥${ledger.totalCosts.macinery.toLocaleString()}`,
    `¥${ledger.totalCosts.industrialWaste.toLocaleString()}`,
    `¥${ledger.totalCosts.other.toLocaleString()}`,
    `¥${ledger.totalCosts.total.toLocaleString()}`,
    `¥${ledger.grossProfit.toLocaleString()}`,
  ]);

  return <Layout>
    <BackLink link='/' label='トップ'></BackLink>
    <H2>工事着工表</H2>
    <List
      type='ledger'
      conditions={ledgerConditions}
      indexes={ledgerIndexes}
      stickyColumns={1}
      rows={rows}
      css={style.table}
    />
  </Layout>;
};

export default ScheduleList;
